import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Mail, MailOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

const STATUS_BADGE = {
  sent: { label: "Email envoyé", cls: "bg-[#F0F9F4] text-brand-green" },
  failed: { label: "Email échoué", cls: "bg-[#FFF5F6] text-brand-red" },
  skipped: { label: "Email non configuré", cls: "bg-elevated text-mute" },
};

/** Console admin : messages du formulaire de contact (copie locale des emails). */
export default function ContactMessagesPanel() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = () => {
    setLoading(true);
    apiClient.get("/admin/contact-messages")
      .then(({ data }) => setMessages(data))
      .catch(() => toast.error(t("admin.cmLoadFail")))
      .finally(() => setLoading(false));
  };
  useEffect(fetchMessages, []);

  const toggleRead = async (m) => {
    try {
      const { data } = await apiClient.patch(`/admin/contact-messages/${m.message_id}/read`);
      setMessages((prev) => prev.map((x) => (x.message_id === m.message_id ? data : x)));
    } catch {
      toast.error(t("admin.cmUpdateFail"));
    }
  };

  return (
    <div data-testid="admin-messages-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold">
          Messages de contact {messages.filter((m) => !m.read).length > 0 && (
            <span className="ml-1 text-xs rounded-full bg-brand-red text-white px-2 py-0.5 align-middle" data-testid="messages-unread-count">
              {messages.filter((m) => !m.read).length} non lu(s)
            </span>
          )}
        </h3>
        <button onClick={fetchMessages} data-testid="messages-refresh" className="rounded-full border border-sand hover:border-ink p-2" aria-label="Rafraîchir">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-mute">Chargement…</p>
      ) : messages.length === 0 ? (
        <p className="text-sm text-mute" data-testid="messages-empty">{t("admin.cmEmpty")}</p>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => {
            const badge = STATUS_BADGE[m.email_status] || STATUS_BADGE.skipped;
            return (
              <div key={m.message_id} data-testid={`contact-msg-${m.message_id}`} className={`rounded-2xl border p-4 ${m.read ? "border-sand bg-white" : "border-brand-red/30 bg-[#FFFDF5]"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {m.name} <span className="text-mute font-normal">· {m.email}</span>
                    </div>
                    <div className="text-[11px] text-mute">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-1 ${badge.cls}`} title={m.email_error || ""}>{badge.label}</span>
                    <button
                      onClick={() => toggleRead(m)}
                      data-testid={`msg-toggle-read-${m.message_id}`}
                      title={m.read ? "Marquer comme non lu" : "Marquer comme lu"}
                      className="rounded-full border border-sand hover:border-ink p-1.5"
                    >
                      {m.read ? <MailOpen className="w-4 h-4 text-mute" /> : <Mail className="w-4 h-4 text-brand-red" />}
                    </button>
                  </div>
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap break-words">{m.message}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
